import { useCallback, useEffect, useRef, useState } from "react";
import { isRequestCanceled } from "../../helpers/abortableRequest";
import { getResourceAssignments, getResourceBudget, getResourceCard, getResourcePerformance } from "./resourceInsights.api";

const ASSIGNMENTS_PAGE_SIZE = 50;

/**
 * Loads the profile of a resource (who they are, their skills, projects and task counts) and reloads it when the
 * project scope or the period changes.
 * `error.response.status` is 404 when the resource is not available to the current user.
 */
export function useResourceCard({ resourceId, projectId, startDate, endDate }) {
  const [state, setState] = useState({ status: "loading", card: null, error: null });
  const [reloadCount, setReloadCount] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setState((previous) => ({ ...previous, status: "loading", error: null }));

    getResourceCard(resourceId, { projectId, startDate, endDate }, controller.signal)
      .then((card) => setState({ status: "ready", card, error: null }))
      .catch((error) => {
        if (isRequestCanceled(error)) return;
        setState({ status: "error", card: null, error });
      });

    return () => controller.abort();
  }, [resourceId, projectId, startDate, endDate, reloadCount]);

  const reload = useCallback(() => setReloadCount((count) => count + 1), []);

  return { ...state, reload };
}

/**
 * Loads the performance indices of a resource for the period of Resource Insights. It is asked for separately from the
 * profile so the panel shows at once while the indices, which take longer to work out, follow. Nothing is asked for while
 * `enabled` is false.
 */
export function useResourcePerformance({ resourceId, projectId, startDate, endDate, enabled }) {
  const [state, setState] = useState({ status: "loading", performance: null });
  const [reloadCount, setReloadCount] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;

    const controller = new AbortController();
    setState((previous) => ({ ...previous, status: "loading" }));

    getResourcePerformance(resourceId, { projectId, startDate, endDate }, controller.signal)
      .then((performance) => setState({ status: "ready", performance }))
      .catch((error) => {
        if (isRequestCanceled(error)) return;
        setState({ status: "error", performance: null });
      });

    return () => controller.abort();
  }, [resourceId, projectId, startDate, endDate, enabled, reloadCount]);

  const reload = useCallback(() => setReloadCount((count) => count + 1), []);

  return { ...state, reload };
}

/**
 * Loads how a resource stands against the hours forecast for them, project by project. It is
 * cumulative, so it only depends on the resource and the project scope, not on the period of Resource Insights. Nothing is
 * asked for while `enabled` is false.
 */
export function useResourceBudget({ resourceId, projectId, enabled }) {
  const [state, setState] = useState({ status: "loading", budget: null });
  const [reloadCount, setReloadCount] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;

    const controller = new AbortController();
    setState((previous) => ({ ...previous, status: "loading" }));

    getResourceBudget(resourceId, { projectId }, controller.signal)
      .then((budget) => setState({ status: "ready", budget }))
      .catch((error) => {
        if (isRequestCanceled(error)) return;
        setState({ status: "error", budget: null });
      });

    return () => controller.abort();
  }, [resourceId, projectId, enabled, reloadCount]);

  const reload = useCallback(() => setReloadCount((count) => count + 1), []);

  return { ...state, reload };
}

/**
 * Loads the tasks a resource is allocated to, a page at a time. Changing a filter starts again from the first page,
 * and a request that is no longer wanted is cancelled so a slow answer never replaces a newer one.
 *
 * @param query  the filters of the list, as `assignmentQuery` makes them
 */
export function useResourceAssignments({ resourceId, query }) {
  // The filters are compared by what they say, so a new object with the same content does not load again.
  const queryKey = JSON.stringify(query);
  const [state, setState] = useState({
    items: [],
    totalCount: 0,
    pageNumber: 0,
    hasMore: false,
    isLoading: true,
    isLoadingMore: false,
    error: null,
  });
  const controllerRef = useRef(null);

  const loadPage = useCallback(
    (pageNumber) => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;

      setState((previous) => ({
        ...previous,
        items: pageNumber === 1 ? [] : previous.items,
        isLoading: pageNumber === 1,
        isLoadingMore: pageNumber > 1,
        error: null,
      }));

      getResourceAssignments(resourceId, { ...JSON.parse(queryKey), pageNumber, pageSize: ASSIGNMENTS_PAGE_SIZE }, controller.signal)
        .then(({ items, pagination }) =>
          setState((previous) => ({
            items: pageNumber === 1 ? items : [...previous.items, ...items],
            totalCount: pagination?.TotalCount ?? items.length,
            pageNumber,
            hasMore: pagination?.HasNext ?? items.length === ASSIGNMENTS_PAGE_SIZE,
            isLoading: false,
            isLoadingMore: false,
            error: null,
          }))
        )
        .catch((error) => {
          if (isRequestCanceled(error)) return;
          setState((previous) => ({ ...previous, isLoading: false, isLoadingMore: false, error }));
        });
    },
    [resourceId, queryKey]
  );

  useEffect(() => {
    loadPage(1);
    return () => controllerRef.current?.abort();
  }, [loadPage]);

  const { hasMore, isLoading, isLoadingMore, pageNumber } = state;

  const loadMore = useCallback(() => {
    if (!hasMore || isLoading || isLoadingMore) return;
    loadPage(pageNumber + 1);
  }, [hasMore, isLoading, isLoadingMore, pageNumber, loadPage]);

  const reload = useCallback(() => loadPage(1), [loadPage]);

  return { ...state, loadMore, reload };
}
